import { API_CLIENT_ID, API_CLIENT_SECRET, API_ENDPOINTS, getAPIURL } from "../config";
import { http } from "../utils/http";
import { Base64 } from "../utils/utils";
import { store } from "../State/store/configureStore";
import { LOG_OUT, TOKEN_REFRESH_SUCCESS } from "../State/actions/actionTypes";
import { showToaster } from "../State/actions/toaster";
import { devLog } from "../utils/devLog";

const REFRESH_BUFFER_SECONDS = 60; // Refresh 1 minute before expiration
const MAX_TIMEOUT_MS = 2147483647; // setTimeout 32-bit signed max (~24.85 days); larger values fire immediately

class TokenRefreshService {
  constructor() {
    this.refreshTimer = null;
    this.isRefreshing = false;
  }

  /**
   * Schedule a token refresh based on expires_in time
   * @param {number} expiresIn - Token expiration time in seconds
   */
  scheduleRefresh(expiresIn) {
    this.clearRefreshTimer();

    if (!expiresIn || expiresIn <= REFRESH_BUFFER_SECONDS) {
      this.refreshToken();
      return;
    }

    const refreshInMs = Math.min(
      (expiresIn - REFRESH_BUFFER_SECONDS) * 1000,
      MAX_TIMEOUT_MS,
    );

    this.refreshTimer = setTimeout(() => {
      this.refreshToken();
    }, refreshInMs);
  }

  /**
   * Clear any pending refresh timer
   */
  clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Detect if the current session is Entra-authenticated. Relies on the
   * explicit authMethod flag set during Entra login in EntraIdCallback /
   * EntraIdFacilitySelector and persisted via redux-persist. A JWT-shape
   * heuristic previously lived here as a fallback, but it misfires now that
   * the password-grant backend also issues JWTs — any non-Entra user would
   * have been misrouted to /api/auth/entra/refresh and kicked on next 401.
   */
  _isEntraSession() {
    const state = store.getState();
    return (
      state.user?.data?.authMethod === "entra_id" ||
      state.user?.authMethod === "entra_id"
    );
  }

  async refreshToken() {
    if (this.isRefreshing) {
      return;
    }

    const state = store.getState();
    const refreshToken = state.user?.refreshToken;
    // For Entra sessions, use the Entra refresh endpoint
    if (this._isEntraSession()) {
      return this._refreshEntraToken(refreshToken);
    }

    if (!refreshToken) {
      return;
    }

    this.isRefreshing = true;

    try {
      const data = this.convertToFormData({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        client_id: API_CLIENT_ID,
        client_secret: API_CLIENT_SECRET,
      });

      const response = await http.request(API_ENDPOINTS.get_token, "post", data, {
        Authorization:
          "Basic " + Base64.encode(API_CLIENT_ID + ":" + API_CLIENT_SECRET),
        "Content-type": "application/x-www-form-urlencoded; charset=utf-8",
      });

      if (response && response.access_token) {
        // Update the token in axios headers
        http.setToken(response.access_token);

        // Dispatch success action to update Redux state
        store.dispatch({
          type: TOKEN_REFRESH_SUCCESS,
          token: response.access_token,
          refreshToken: response.refresh_token,
          expiresIn: response.expires_in,
        });

        // Schedule next refresh
        this.scheduleRefresh(response.expires_in);
      } else {
        throw new Error("No access token in refresh response");
      }
    } catch (error) {
      // If refresh fails, log the user out
      store.dispatch({ type: LOG_OUT, message: "Session expired. Please login again." });
      store.dispatch(
        showToaster("Session expired. Please login again.", "error", 7)
      );
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Refresh an Entra-issued token via the Entra refresh endpoint.
   * Falls back to no-op if no refresh token (Entra tokens may have longer lifetimes).
   */
  async _refreshEntraToken(refreshToken) {
    if (!refreshToken) {
      return;
    }

    this.isRefreshing = true;

    try {
      // Endpoint is permitAll — no Authorization header needed (token may be expired)
      const response = await fetch(`${getAPIURL()}/api/auth/entra/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.status === 403) {
        // User removed from Azure AD groups — force logout
        devLog("TokenRefresh: Entra user no longer has active spaces (403)");
        store.dispatch({ type: LOG_OUT });
        return;
      }

      if (!response.ok) {
        throw new Error(`Entra token refresh failed (${response.status})`);
      }

      const data = await response.json();
      const newToken = data.accessToken || data.access_token;

      if (newToken) {
        http.setToken(newToken);

        store.dispatch({
          type: TOKEN_REFRESH_SUCCESS,
          token: newToken,
          refreshToken: data.refreshToken || data.refresh_token || refreshToken,
          expiresIn: data.expiresIn || data.expires_in,
        });

        if (data.expiresIn || data.expires_in) {
          this.scheduleRefresh(data.expiresIn || data.expires_in);
        }
      }
    } catch (error) {
      devLog("TokenRefresh: Entra refresh failed:", error);
      // Don't log out immediately — Entra tokens may still be valid
      // Only log out if API calls actually start failing with 401
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Convert object to URL-encoded form data
   * @param {Object} data - Object to convert
   * @returns {string} URL-encoded string
   */
  convertToFormData(data) {
    return Object.entries(data)
      .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
      .join("&");
  }

  /**
   * Initialize token refresh on app startup/rehydration
   * Called when user data is restored from storage
   * @param {string} refreshToken - The stored refresh token
   * @param {number} expiresIn - Original expires_in value (if available)
   * @param {number} tokenTimestamp - When the token was originally obtained
   */
  initializeFromStorage(refreshToken, expiresIn, tokenTimestamp) {
    if (!refreshToken) {
      return;
    }

    // Calculate remaining time if we have timestamp info
    if (tokenTimestamp && expiresIn) {
      const now = Date.now();
      const tokenAge = Math.floor((now - tokenTimestamp) / 1000);
      const remainingTime = expiresIn - tokenAge;

      if (remainingTime <= REFRESH_BUFFER_SECONDS) {
        // Token is expired or about to expire, refresh immediately
        this.refreshToken();
      } else {
        // Schedule refresh for remaining time
        this.scheduleRefresh(remainingTime);
      }
    } else {
      // No timestamp info, refresh immediately to be safe
      this.refreshToken();
    }
  }

  /**
   * Stop all refresh activities (on logout)
   */
  stop() {
    this.clearRefreshTimer();
    this.isRefreshing = false;
  }
}

const tokenRefreshService = new TokenRefreshService();
export default tokenRefreshService;
